import React from 'react';
import { ListColumnType, ListFilterFieldType, ListFilterType, ListMoreMenuConfig } from 'types/ListTypes';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import AbstractList from 'components/lists/AbstractList';
import InvoiceModal from './InvoiceModal';
import { useSettings } from 'services/settings/SettingsContext';
import { renderDate, renderDocumentNumber, renderPriceDecimalColumn, renderStatusBadge } from 'components/lists/functions/renderListColumn';

// Enable the bulk actions panel for this list
const invoiceBulkActions: BulkActionsConfig = {
    objectKey: 'invoice',
    deleteOrDeactivate: 'delete',
    showExport: true,
};

// Configure the more menu of this list
const invoiceMoreMenu: ListMoreMenuConfig = {
    inactiveItemsToggle: {
        semantic: 'deleted',
        activeParams: { deleted: true },
        clearFilterNames: ['status'],
    },
};

// Set the filter fields of this list
const invoiceFilterFields: ListFilterFieldType[] = [
    {
        type: 'search',
        name: 'search',
        label: 'forms.search_label',
        placeholder: 'forms.search_placeholder',
        queryParam: 'search',
        width: 1.5
    },
    {
        type: 'dropdown',
        name: 'workflow',
        label: 'lists.filters.type_label',
        queryParam: 'workflow',
        apiRequest: {
            endpoint: 'get_workflow_list',
            params: { connected_feature: 'invoice', is_active: true },
            object: 'workflow'
        },
        groupByField: 'division',
        width: 1.25
    },
    {
        type: 'dropdown',
        name: 'status',
        label: 'lists.filters.status_label',
        queryParam: 'status',
        backendField: 'id',
        optionField: 'id',
        translationPrefix: 'invoice.status.',
        hardcodedOptions: [
            { id: 'draft' },
            { id: 'open' },
            { id: 'overdue' },
            { id: 'reminded' },
            { id: 'paid' },
        ],
        width: 1.25
    },
    {
        type: 'daterange',
        name: 'invoice_date_range',
        label: 'invoice.general.date_label',
        queryParam: 'invoice_date',
        width: 2
    },
    {
        type: 'dropdown',
        name: 'division',
        label: 'lists.filters.division_label',
        queryParam: 'division',
        requiredFeature: 'divisions',
        defaultValueFromContext: 'user_division',
        apiRequest: {
            endpoint: 'get_division_list',
            params: { is_active: true },
            object: 'division'
        },
        width: 1.5
    }
];

// Set the filters for the invoice detail list, the main invoice list uses the filter fields above
export const invoiceFilters: ListFilterType[] = [
    {
        id: 'active',
        label: 'invoice.general.all_invoices_filter_label',
        params: { 'deleted': false },
        default: true,
    },
    {
        group: 'invoice.general.status_filter_group_label',
        filters: [
            {
                id: 'draft',
                label: 'invoice.status.draft',
                params: { 'status': 'draft' },
            },
            {
                id: 'open',
                label: 'invoice.status.open',
                params: { 'status': 'open' },
            },
            {
                id: 'overdue',
                label: 'invoice.status.overdue',
                params: { 'status': 'overdue' },
            },
            {
                id: 'reminded',
                label: 'invoice.status.reminded',
                params: { 'status': 'reminded' },
            },
            {
                id: 'paid',
                label: 'invoice.status.paid',
                params: { 'status': 'paid' },
            },
        ]
    },
    {
        group: 'general.archive',
        filters: [
            {
                id: 'deleted',
                label: 'invoice.general.deleted_invoices_label',
                shortName: 'general.deleted',
                params: { 'deleted': true },
            }
        ]   
    }
]

// Rename the name of the function:
const InvoiceList: React.FC = () => {
    const { userLocale } = useSettings();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_invoice_list',
        defaultParams: { 'deleted': false },
        object: 'invoice'
    }
    const objectName = 'invoice.general.object_name'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list
    const postNewItem = { url: 'post_invoice' }

    const detailModal = <InvoiceModal />;
    const onRowClick = 'detailModal'  

    // Specify the columns of this list
    const columns: ListColumnType[] = [
        { 
            width: '1.5fr', 
            field: 'number', 
            label: 'invoice.general.invoice_number_label', 
            render: (_, row) => renderDocumentNumber(row.number, row.status, 'invoice') 
        },
        { 
            width: '3fr', 
            field: 'internal_reference', 
            label: 'invoice.general.internal_reference_label' 
        },
        { 
            width: '2fr', 
            field: 'invoice_date', 
            label: 'invoice.general.date_label', 
            render: (_, row) => renderDate(row.invoice_date, userLocale) 
        },
        { 
            width: '4fr', 
            field: 'recipient_name',
            orderField: 'recipient__name', 
            label: 'invoice.general.recipient_label' 
        },
        { 
            width: '1.5fr', 
            field: 'amount', 
            label: 'invoice.general.amount_label', 
            render: (_, row) => renderPriceDecimalColumn(row.amount, row.currency, userLocale) 
        },
        { 
            width: '144px', 
            field: 'status', 
            label: 'invoice.general.status_label', 
            disableOrdering: true,
            render: (_, row) => renderStatusBadge(row.status) 
        }
    ];

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            filterFields={invoiceFilterFields}
            postNewItem={postNewItem}
            detailModal={detailModal}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            bulkActions={invoiceBulkActions}
            moreMenu={invoiceMoreMenu}
        />
    );
};

export default InvoiceList;